import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { HOW_TO_PLAY_PATH } from "../../utils/playerNavigation";
import { apiUrl } from "../../utils/apiUrl";
import { categoryIconUrl } from "../../utils/themeAssets";
import { uploadYogaMediaViaPresign } from "../../utils/yogaMediaUpload";
import { addReportToServer } from "../../functions/sendReport";
import { isDemoUser } from "../../utils/yogaResumeRoute";
import {
  ensureFontsFromSectionStyles,
  parseSectionStyles,
  textStyleToCss,
} from "../../utils/sectionStyles";
import { ensureGoogleFont } from "../../admin/components/TextStyleControls";
import "../../components/stage/stageShell.css";
import "./game.css";

const MAX_BYTES = 50 * 1024 * 1024;
const ALLOWED = /\.(jpe?g|png|mp4|mov)$/i;
const STEPS = ["form", "category", "upload"];

function appBaseUrl() {
  const b = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
  return b || (typeof window !== "undefined" ? window.location.origin : "");
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

const isTrue = (v) => String(v ?? "").trim().toLowerCase() === "true";
const firstEntry = (obj) => Object.entries(obj || {})[0] || [];
const objArrToMap = (arr) =>
  (Array.isArray(arr) ? arr : []).reduce((acc, o) => {
    const [k, v] = firstEntry(o);
    if (k != null) acc[k] = v;
    return acc;
  }, {});

const normalizeKey = (name) =>
  String(name || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

function titleCasePlaceholder(name) {
  return String(name || "")
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildCategories(themeData) {
  const names = Array.isArray(themeData?.default_category_name)
    ? themeData.default_category_name
    : [];
  const icons = Array.isArray(themeData?.category_icons) ? themeData.category_icons : [];
  const descMap = objArrToMap(themeData?.category_placeholder_text);
  const descEnabled =
    String(themeData?.category_description ?? "").trim().toLowerCase() === "true";

  return names
    .map((name, i) => {
      const [iconKey, iconVal] = firstEntry(icons[i]);
      const key = iconKey || normalizeKey(name);
      if (String(name).toUpperCase() === "ALL") return null;
      return {
        name: String(name || ""),
        key,
        icon: String(iconVal || "").trim(),
        description: descEnabled ? String(descMap[key] ?? "") : "",
      };
    })
    .filter(Boolean);
}

function asRuleList(value) {
  if (Array.isArray(value) && value.length) return value.map((v) => String(v ?? ""));
  if (typeof value === "string" && value.trim()) {
    try {
      const p = JSON.parse(value);
      if (Array.isArray(p) && p.length) return p.map((v) => String(v ?? ""));
    } catch {
      /* ignore */
    }
  }
  return [];
}
export default function GamePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const categories = useMemo(() => buildCategories(themeData), [themeData]);
  const submitRules = useMemo(() => {
    const fromUpload = asRuleList(themeData?.game_upload_rules);
    if (fromUpload.length && fromUpload.some((r) => String(r).trim())) {
      return fromUpload.filter((r) => String(r).trim());
    }
    const legacy = asRuleList(themeData?.submit_your_talent_rules);
    return legacy.filter((r) => String(r).trim());
  }, [themeData]);

  const uploadRulesStyles = useMemo(() => {
    const styles = parseSectionStyles(themeData?.section_styles, {
      font_family: themeData?.font_family,
      font_size_desktop: themeData?.font_size_desktop,
      font_size_mobile: themeData?.font_size_mobile,
      rules_text_color: themeData?.rules_text_color,
      text_color: themeData?.text_color,
    });
    ensureFontsFromSectionStyles(styles, ensureGoogleFont);
    const isMobile =
      typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
    return {
      title: textStyleToCss(styles.game_upload?.title, { isMobile }),
      rules: textStyleToCss(styles.game_upload?.rules, { isMobile }),
    };
  }, [themeData]);
  const additionalOn = isTrue(themeData?.additional_fields);
  const extraFieldDefs = useMemo(() => {
    if (!additionalOn) return [];
    const list = Array.isArray(themeData?.additional_new_fields)
      ? themeData.additional_new_fields
      : [];
    return list
      .filter((f) => f && f.selected)
      .map((f) => ({
        name: String(f.name || "").trim(),
        type: String(f.type || "text").trim().toLowerCase(),
        required: Boolean(f.required),
      }))
      .filter((f) => f.name);
  }, [additionalOn, themeData]);

  // Form upload button (badges_share_status) — default enabled when unset
  const shareEnabled = useMemo(() => {
    const raw = themeData?.badges_share_status;
    if (raw == null || raw === "") return true;
    const s = String(raw).trim().toLowerCase();
    return s !== "false" && s !== "0" && s !== "closed" && s !== "disabled";
  }, [themeData?.badges_share_status]);

  const allowMultipleShares = isTrue(themeData?.badges_share_multiple);
  const shareLimit = useMemo(() => {
    if (!allowMultipleShares) return 1;
    const n = Number.parseInt(String(themeData?.badges_share_limit ?? "1"), 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 10);
  }, [allowMultipleShares, themeData?.badges_share_limit]);

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [extraValues, setExtraValues] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [fileEntry, setFileEntry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const reportIdRef = useRef(null);
  const demoRedirectHandledRef = useRef(false);

  const redirectDemoToPlans = useCallback(() => {
    if (demoRedirectHandledRef.current) return;
    demoRedirectHandledRef.current = true;
    sessionStorage.setItem("spotThankYouMsg", "Thanks for playing (demo).");
    window.setTimeout(() => {
      Swal.fire({
        icon: "success",
        title: "Thank you for playing.",
        text: "Subscribe to any PLAN to play with your peers.",
      }).then(() => {
        window.location.href = demoPlansUrl();
      });
    }, 0);
  }, []);

  useEffect(() => {
    if (user?.reportId && !reportIdRef.current) {
      reportIdRef.current = user.reportId;
    }
  }, [user?.reportId]);

  useEffect(() => {
    dispatch(setBackButtonUrl(HOW_TO_PLAY_PATH));
  }, [dispatch]);

  useEffect(() => {
    if (user?.name && !name) setName(String(user.name));
  }, [user, name]);

  // Redirect to gallery only when form upload is off, or no remaining upload slots.
  // When multiple uploads are allowed, players may return to submit again (up to shareLimit total).
  useEffect(() => {
    if (!adminToken || !user?.userId || status !== "succeeded") return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: user.userId }),
        });
        const json = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (typeof json?.canUploadMore === "boolean") {
          if (!json.canUploadMore) {
            navigate("/gallery", { replace: true });
          }
          return;
        }
        // Fallback for older API
        if (json?.hasUpload && (!shareEnabled || !allowMultipleShares)) {
          navigate("/gallery", { replace: true });
        }
      } catch {
        /* allow submit flow */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    adminToken,
    user?.userId,
    navigate,
    status,
    shareEnabled,
    allowMultipleShares,
  ]);

  const onPickFile = (file) => {
    if (!file) return;
    if (!ALLOWED.test(file.name)) {
      Swal.fire("Invalid file", "Use JPG, PNG, MP4, or MOV (max 50 MB).", "warning");
      return;
    }
    if (file.size > MAX_BYTES) {
      Swal.fire("File too large", "Maximum file size is 50 MB.", "warning");
      return;
    }
    const isVideo = /^video\//.test(file.type) || /\.(mp4|mov)$/i.test(file.name);
    setFileEntry({
      file,
      previewUrl: URL.createObjectURL(file),
      isVideo,
    });
  };

  useEffect(() => {
    const url = fileEntry?.previewUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileEntry?.previewUrl]);

  const validateStep = (idx) => {
    if (idx === 0) {
      if (!String(name).trim()) {
        Swal.fire("Required", "Please enter your name.", "warning");
        return false;
      }
      const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      for (const f of extraFieldDefs) {
        const label = titleCasePlaceholder(f.name);
        const raw = String(extraValues[f.name] ?? "").trim();
        const type = f.type || "text";
        if (f.required && !raw) {
          Swal.fire("Required", `Please enter ${label}.`, "warning");
          return false;
        }
        if (!raw) continue;
        if (type === "email" || f.name === "email") {
          if (!EMAIL_RE.test(raw)) {
            Swal.fire("Invalid email", `Enter a valid email for ${label}.`, "warning");
            return false;
          }
        } else if (type === "phone" || f.name === "phone" || f.name === "mobile") {
          const digits = raw.replace(/\D/g, "");
          if (!/^\d{10}$/.test(digits)) {
            Swal.fire(
              "Invalid phone",
              `${label} must be a 10-digit numeric mobile number.`,
              "warning"
            );
            return false;
          }
        } else if (type === "employee_id" || f.name === "employee_id") {
          if (raw.length < 2) {
            Swal.fire("Invalid employee ID", `Enter a valid ${label}.`, "warning");
            return false;
          }
        }
      }
      return true;
    }
    if (idx === 1) {
      if (!selectedCategory) {
        Swal.fire("Required", "Please select a category.", "warning");
        return false;
      }
      if (!String(title).trim()) {
        Swal.fire("Required", "Please enter an entry title.", "warning");
        return false;
      }
      if (String(title).trim().length > 50) {
        Swal.fire("Too long", "Entry title must be 50 characters or less.", "warning");
        return false;
      }
      if (String(description).trim().length > 250) {
        Swal.fire("Too long", "Description must be 250 characters or less.", "warning");
        return false;
      }
      return true;
    }
    if (idx === 2) {
      if (!fileEntry?.file) {
        Swal.fire("Required", "Please upload your talent file.", "warning");
        return false;
      }
      return true;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const goPrev = () => setStep((s) => Math.max(0, s - 1));

  const handleFinalSubmit = useCallback(async () => {
    if (!validateStep(2)) return;
    if (!shareEnabled) {
      Swal.fire("Uploads closed", "Form upload is currently disabled by the admin.", "info");
      return;
    }
    if (!user || !adminToken) {
      Swal.fire("Session expired", "Please log in again.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const submitResp = await fetch(apiUrl("/talentSubmit"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user.userId,
          name: String(name).trim(),
          email: user.email || "",
          category: selectedCategory.name,
          title: String(title).trim(),
          message: String(title).trim(),
          descriptions: String(description).trim(),
          extraFields: extraValues,
        }),
      });
      const submitJson = await submitResp.json();
      if (!submitResp.ok || !submitJson?.success) {
        throw new Error(submitJson?.error || "Could not save submission.");
      }

      await uploadYogaMediaViaPresign({
        files: [fileEntry.file],
        userId: user.userId,
        time: "00:00",
        token: adminToken,
        gameId: submitJson.gameId,
      });

      // One report per user: first upload ADDs, later uploads UPDATE same reportId.
      // Points = upload count (1, 2, 3…) — default before first upload is effectively 0 (no row).
      const used = submitJson.sharesUsed != null ? Number(submitJson.sharesUsed) : null;
      const uploadCount = Math.max(1, Number.isFinite(used) ? used : 1);
      const categoryName = String(selectedCategory?.name || "").trim();
      const entryTitle = String(title).trim();
      addReportToServer(user, {
        points: uploadCount,
        reportTime: "00:00",
        answers: [categoryName, entryTitle, "upload"].filter(Boolean),
        reportIdRef,
      });

      // Demo users: after first successful upload → plans page (same as YogaPoses).
      if (isDemoUser(user)) {
        redirectDemoToPlans();
        return;
      }

      const lim = submitJson.shareLimit != null ? Number(submitJson.shareLimit) : shareLimit;
      const canSubmitMore =
        allowMultipleShares && used != null && Number.isFinite(used) && used < lim;

      if (canSubmitMore) {
        const again = await Swal.fire({
          title: "Submitted!",
          text: `You can upload ${lim - used} more time(s) in this category. Submit another entry?`,
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Submit another",
          cancelButtonText: "Go to gallery",
        });
        if (again.isConfirmed) {
          setTitle("");
          setDescription("");
          setFileEntry(null);
          setStep(1);
          return;
        }
      }

      navigate("/gallery", { replace: true });
    } catch (e) {
      Swal.fire("Error", e.message || "Submission failed.", "error");
    } finally {
      setSubmitting(false);
    }
  }, [
    adminToken,
    allowMultipleShares,
    description,
    extraValues,
    fileEntry,
    name,
    navigate,
    redirectDemoToPlans,
    selectedCategory,
    shareEnabled,
    shareLimit,
    title,
    user,
  ]);

  if (status === "loading" || status === "idle") {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="stage-shell">
        <p style={{ textAlign: "center" }}>
          Could not load game data.{themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="stage-cta"
          style={{ display: "block", margin: "16px auto" }}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!shareEnabled) {
    return (
      <div className="stage-shell tg-page">
        <div className="stage-inner" style={{ textAlign: "center", maxWidth: 480 }}>
          <h1 className="stage-title">Uploads closed</h1>
          <p className="stage-subtitle">
            Form upload is currently disabled. You can still browse the gallery.
          </p>
          <button
            type="button"
            className="stage-cta"
            onClick={() => navigate("/gallery")}
          >
            Browse gallery
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stage-shell tg-page">
      <div className="stage-inner">
        <h1 className="stage-title">Your submission</h1>
        <p className="stage-subtitle">
          Fill in your details and upload your talent entry
          {allowMultipleShares && shareLimit > 1
            ? ` (up to ${shareLimit} uploads total)`
            : ""}
        </p>

        <div className="tg-slider">
          <button
            type="button"
            className="stage-nav-btn"
            onClick={goPrev}
            disabled={step === 0 || submitting}
            aria-label="Previous"
          >
            ‹
          </button>

          <div className="stage-card tg-card">
            {step === 0 && (
              <div className="tg-step">
                <div className="tg-avatar" aria-hidden>
                  👤
                </div>
                <h2 className="tg-step-title">
                  {themeData?.lable_name_text || "Your name"}
                </h2>
                <input
                  className="tg-input tg-input--readonly"
                  type="text"
                  placeholder="eg: Mihir Kulkarni"
                  value={name}
                  readOnly
                  maxLength={100}
                  aria-readonly="true"
                />
                {extraFieldDefs.map((f) => {
                  const label = titleCasePlaceholder(f.name);
                  const inputType =
                    f.type === "email" || f.name === "email"
                      ? "email"
                      : f.type === "phone" || f.name === "phone" || f.name === "mobile"
                        ? "tel"
                        : "text";
                  return (
                    <label key={f.name} className="tg-field-label">
                      <span className="tg-step-title left">
                        {label}
                        {f.required ? " *" : ""}
                      </span>
                      <input
                        className="tg-input"
                        type={inputType}
                        inputMode={
                          f.type === "phone" || f.name === "phone" || f.name === "mobile"
                            ? "numeric"
                            : undefined
                        }
                        maxLength={
                          f.type === "phone" || f.name === "phone" || f.name === "mobile"
                            ? 10
                            : 100
                        }
                        placeholder={label}
                        value={extraValues[f.name] || ""}
                        onChange={(e) => {
                          let v = e.target.value;
                          if (f.type === "phone" || f.name === "phone" || f.name === "mobile") {
                            v = v.replace(/\D/g, "").slice(0, 10);
                          }
                          setExtraValues((prev) => ({
                            ...prev,
                            [f.name]: v,
                          }));
                        }}
                        required={Boolean(f.required)}
                        aria-required={f.required ? "true" : undefined}
                      />
                    </label>
                  );
                })}
                <button
                  type="button"
                  className="tg-gallery-link"
                  onClick={() => navigate("/gallery")}
                >
                  Browse gallery first →
                </button>
                <p className="tg-gallery-hint">
                  See what others uploaded, then come back to submit yours.
                </p>
              </div>
            )}

            {step === 1 && (
              <div className="tg-step">
                <h2 className="tg-step-title left">Select category</h2>
                <div className="tg-cats">
                  {categories.map((cat) => {
                    const active = selectedCategory?.key === cat.key;
                    return (
                      <button
                        type="button"
                        key={cat.key}
                        className={`tg-cat${active ? " active" : ""}`}
                        onClick={() => setSelectedCategory(cat)}
                        title={cat.description || undefined}
                        aria-label={
                          cat.description
                            ? `${cat.name}: ${cat.description}`
                            : cat.name
                        }
                      >
                        <span className="tg-cat-icon">
                          <img src={categoryIconUrl(cat.icon)} alt="" />
                          {cat.description ? (
                            <span className="tg-cat-tooltip" role="tooltip">
                              {cat.description}
                            </span>
                          ) : null}
                        </span>
                        <span className="tg-cat-label">{cat.name}</span>
                      </button>
                    );
                  })}
                </div>

                <h2 className="tg-step-title left">Entry title *</h2>
                <input
                  className="tg-input"
                  type="text"
                  placeholder="e.g. Mihir's Classical Singing"
                  value={title}
                  maxLength={50}
                  onChange={(e) => setTitle(e.target.value.slice(0, 50))}
                />
                <div className="tg-char-count">{title.length}/50</div>

                <h2 className="tg-step-title left">Short description</h2>
                <textarea
                  className="tg-input tg-textarea"
                  placeholder="Tell us about your talent in 2-3 sentences...."
                  value={description}
                  maxLength={250}
                  rows={4}
                  onChange={(e) => setDescription(e.target.value.slice(0, 250))}
                />
                <div className="tg-char-count">{description.length}/250</div>
              </div>
            )}

            {step === 2 && (
              <div className="tg-step">
                <button
                  type="button"
                  className="tg-dropzone"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    onPickFile(e.dataTransfer?.files?.[0]);
                  }}
                >
                  <span className="tg-upload-icon">☁↑</span>
                  <strong>Tap to upload</strong>
                  {!fileEntry?.file && (
                    <>
                      <span>Video, image, or document · Max 50 MB</span>
                      <div className="tg-formats">
                        {["MP4", "JPG", "PNG", "MOV"].map((t) => (
                          <span key={t}>{t}</span>
                        ))}
                      </div>
                    </>
                  )}
                  {fileEntry?.file && (
                    <div className="tg-file-name">{fileEntry.file.name}</div>
                  )}
                  {fileEntry?.previewUrl && (
                    <div className="tg-file-preview" onClick={(e) => e.stopPropagation()}>
                      {fileEntry.isVideo ? (
                        <video src={fileEntry.previewUrl} controls playsInline />
                      ) : (
                        <img src={fileEntry.previewUrl} alt="Selected upload preview" />
                      )}
                    </div>
                  )}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.mp4,.mov"
                  hidden
                  onChange={(e) => {
                    onPickFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <div className="tg-rules">
                  <h3 style={uploadRulesStyles.title || undefined}>Rules</h3>
                  <ul style={uploadRulesStyles.rules || undefined}>
                    {(submitRules.length
                      ? submitRules
                      : [
                          "Choose your talent category — music, dance, art, comedy, photography, cooking, or other.",
                          "Upload a video, image, or written description of your talent.",
                          "Add a title and a short description to help the audience connect.",
                          "Hit submit — your entry goes to the talent gallery!",
                        ]
                    ).map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className="stage-nav-btn"
            onClick={() => {
              if (step !== STEPS.length - 1) goNext();
            }}
            disabled={submitting || step === STEPS.length - 1}
            aria-label="Next"
          >
            ›
          </button>
        </div>

        <div className="stage-dots">
          {STEPS.map((_, i) => (
            <button
              key={STEPS[i]}
              type="button"
              className={`stage-dot${i === step ? " active" : ""}`}
              onClick={() => {
                if (i < step || validateStep(step)) setStep(i);
              }}
              aria-label={`Step ${i + 1}`}
            />
          ))}
        </div>

        {step === STEPS.length - 1 && (
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <button
              type="button"
              className="stage-cta"
              disabled={submitting || !shareEnabled}
              onClick={handleFinalSubmit}
            >
              {submitting ? "Submitting…" : "Submit entry"}
            </button>
          </div>
        )}
      </div>
      {/* <div className="stage-platform" aria-hidden /> */}
    </div>
  );
}
